/** API client — defaults to live workers.dev, prefers api.gdpr-nordic.org when available */
const DEFAULT =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL) ||
  "https://api.gdpr-nordic.org";

export function apiBase(): string {
  return DEFAULT.replace(/\/$/, "");
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || res.statusText);
  }
  return data as T;
}

export type ScanRequest = {
  fullName: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
};

export type Exposure = {
  id: string;
  sourceType?: string;
  sourceId: string;
  sourceName: string;
  site: string;
  url: string;
  status: string;
  strategy?: string;
  severity?: string;
  snippet?: string;
  subjectId?: string;
  mentionedOnPage?: boolean;
  mailto?: string;
  optOutUrl?: string;
  removalHelp?: string;
};

/** Saved exposures for the signed-in (or demo) account. */
export async function listExposures(subjectId?: string) {
  const q = subjectId ? `?subjectId=${encodeURIComponent(subjectId)}` : "";
  return req<{ data: Exposure[]; meta?: { total?: number; demo?: boolean } }>(
    `/v1/exposures${q}`,
    { headers: authHeaders() }
  );
}

export type Ticket = {
  id: string;
  caseId: string;
  exposureId?: string;
  status: string;
  sourceName: string;
  strategy: string;
  mailto?: string | null;
  guideUrl?: string | null;
  emailDraft?: { to?: string; subject?: string; body?: string } | null;
  timeline?: { at: string; status: string; note?: string }[];
};

export async function health() {
  return req<{ ok: boolean; mvp?: boolean }>(`/health`);
}

export async function scan(body: ScanRequest) {
  return req<{
    data: {
      subject: { id: string; fullName: string };
      scan: { id: string; counts: Record<string, number> };
      exposures: Exposure[];
    };
    meta?: { persisted?: boolean; note?: string };
    // authHeaders() med flit även här: scanen fungerar utan inloggning, men
    // träffarna sparas bara om vi vet vems de är.
  }>(`/v1/scan`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
}

export async function requestRemovals(subjectId: string, exposureIds: string[]) {
  return req<{ data: { tickets: Ticket[] }; meta: unknown }>(`/v1/removals`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ subjectId, exposureIds }),
  });
}

/** Utan subjectId returneras alla ärenden på kontot. Servern filtrerar alltid
 *  på inloggad användare — ett subjectId som tillhör någon annan ger tomt. */
export async function listTickets(subjectId?: string) {
  const q = subjectId ? `?subjectId=${encodeURIComponent(subjectId)}` : "";
  return req<{ data: Ticket[] }>(`/v1/tickets${q}`, { headers: authHeaders() });
}

export type LexbasePackage = {
  to: string;
  deadline: string;
  protectedResidence: boolean;
  followUpEmail: { to: string; subject: string; body: string };
  fullLetter: string;
  mailto: string;
  checklist: string[];
  disclaimer: string;
  legalRefs?: string[];
};

export type LexbaseInput = {
  fullName: string;
  email: string;
  protectedResidence?: boolean;
  altNames?: string[];
  deadline?: string;
};

export async function lexbasePreview(body: LexbaseInput) {
  return req<{ data: LexbasePackage }>(`/v1/legal/lexbase/preview`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function lexbaseOpen(body: LexbaseInput) {
  return req<{
    data: {
      subject: { id: string; fullName: string };
      ticket: {
        id: string;
        caseId: string;
        package: LexbasePackage;
        mailto: string;
        deadline: string;
      };
    };
  }>(`/v1/legal/lexbase/open`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Store-reviewer / sales demo — fixed fictional profile (Anna Exempel) */
export const DEMO_EMAIL = "demo@gdpr-nordic.org";
export const DEMO_PASSWORD = "DemoReview2026!";

export type DemoAuthResponse = {
  token: string;
  tokenType: string;
  expiresIn: number;
  user: { email: string; name: string; firstName?: string; isDemo: boolean };
  profile?: unknown;
  meta?: { demo?: boolean; stale?: boolean; note?: string };
};

export async function loginDemo() {
  return req<DemoAuthResponse>(`/v1/auth/demo`, { method: "POST", body: "{}" });
}

// ── Auth: magic link ──────────────────────────────────────────────────────
// Den gamla /v1/auth/login är borttagen ur API:t. Den accepterade vilken
// e-post som helst med vilket lösenord som helst och gav en token som aldrig
// validerades. Inloggning sker nu med engångslänk via e-post.

const SESSION_KEY = "gdpr_nordic_session";
const DEMO_KEY = "gdpr_nordic_demo_session";

export type Session = {
  token: string;
  expiresAt: number;
  user: { id: string; email: string; name?: string | null };
};

export function saveSession(s: Session) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    /* private mode */
  }
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (!s?.token || (s.expiresAt && s.expiresAt < Date.now())) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* */
  }
}

/**
 * Demo-token låg i sessionStorage under en egen nyckel, medan authHeaders
 * bara läste den vanliga sessionen i localStorage. Följden: granskarens
 * demoinloggning "lyckades" men varje efterföljande anrop gick utan token
 * och svarade 401. Apple och Google bedömer appen på just den vägen.
 */
function demoToken(): string | null {
  try {
    const raw = sessionStorage.getItem(DEMO_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as { token?: string }).token ?? null;
  } catch {
    return null;
  }
}

export function authHeaders(): Record<string, string> {
  // Demo flag wins: a leftover real session must not hide Anna Exempel.
  if (isDemoSession()) {
    const demo = demoToken();
    if (demo) return { Authorization: `Bearer ${demo}` };
  }
  const s = loadSession();
  if (s) return { Authorization: `Bearer ${s.token}` };
  const demo = demoToken();
  return demo ? { Authorization: `Bearer ${demo}` } : {};
}

/** Steg 1: begär en engångslänk. Svarar likadant även om kontot inte finns. */
export async function requestMagicLink(email: string) {
  return req<{
    data?: { sent: boolean; devLink?: string };
    // demo@ shortcut returnerar token på rotnivå — aldrig blanda ihop med vanlig länk.
    token?: string;
    user?: DemoAuthResponse["user"];
    meta?: { note?: string; demo?: boolean };
  }>(`/v1/auth/request-link`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/** Steg 2: lös in token från länken och få en session. */
export async function completeMagicLink(token: string): Promise<Session> {
  const res = await req<{
    token: string;
    expiresAt: number;
    user: { id: string; email: string; name?: string | null };
  }>(`/v1/auth/callback`, {
    method: "POST",
    body: JSON.stringify({ token, platform: "web" }),
  });
  // Rensa eventuellt kvarhängande demo-flagga från tidigare granskarinloggning.
  clearDemoSession();
  const session: Session = { token: res.token, expiresAt: res.expiresAt, user: res.user };
  saveSession(session);
  return session;
}

export async function logout() {
  try {
    await req(`/v1/auth/logout`, { method: "POST", headers: authHeaders(), body: "{}" });
  } catch {
    /* lokalt utloggad ändå */
  }
  clearSession();
  clearDemoSession();
}

export async function fetchMe() {
  return req<{ data: MeResponse }>(`/v1/me`, { headers: authHeaders() });
}

export type MeResponse = {
  id: string;
  email: string;
  name: string | null;
  locale: string;
  createdAt: number;
  entitlement: {
    plan: string | null;
    planLabel: string;
    products: string[];
    maxSubjects: number;
    canRemove: boolean;
    canLexbase: boolean;
    expiresAt: number | null;
  };
  subjects: { used: number; max: number };
};

// ── Art. 15 / 20: dataexport ─────────────────────────────────────────────

/** Laddar ned hela datakopian som JSON-fil. */
export async function downloadMyData(): Promise<void> {
  const res = await fetch(`${apiBase()}/v1/me/export`, { headers: authHeaders() });
  if (!res.ok) throw new Error(res.status === 401 ? "Du måste vara inloggad." : "Export misslyckades.");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `gdpr-nordic-mina-uppgifter-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Art. 17: radering ────────────────────────────────────────────────────

/** Raderar kontot på riktigt. Kräver bekräftelseordet, precis som API:t. */
export async function deleteMyAccount(): Promise<{ deleted: boolean }> {
  const res = await fetch(`${apiBase()}/v1/me`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ confirm: "RADERA" }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    data?: { deleted: boolean };
    error?: string;
  };
  if (!res.ok) throw new Error(data.error || "Raderingen misslyckades.");
  clearSession();
  clearDemoSession();
  return data.data ?? { deleted: true };
}

// ── Lexbase ──────────────────────────────────────────────────────────────

export type LexbaseLetter = {
  caseId: string;
  ticketId: string;
  recipient: string;
  company: string;
  subject: string;
  body: string;
  deadline: string;
  unfilledPlaceholders: string[];
};

export async function generateLexbaseLetter(input: {
  fullName: string;
  email?: string;
  address?: string;
  lexbaseUrl: string;
  protectedResidence?: boolean;
}) {
  return req<{
    data: LexbaseLetter;
    meta: { aiGenerated: boolean; fallbackUsed: boolean; notice: string };
  }>(`/v1/legal/lexbase/generate`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
}

export async function fetchDemoProfile() {
  return req<{ data: unknown; credentials: { email: string; password: string } }>(
    `/v1/demo/profile`
  );
}

export function saveDemoSession(token: string, user: DemoAuthResponse["user"]) {
  try {
    // Drop any real customer session so /demo always shows the full stale profile.
    clearSession();
    sessionStorage.setItem(
      DEMO_KEY,
      JSON.stringify({ token, user, at: new Date().toISOString() })
    );
    localStorage.setItem("gdpr_nordic_is_demo", "1");
  } catch {
    /* private mode */
  }
}

export function clearDemoSession() {
  try {
    sessionStorage.removeItem(DEMO_KEY);
    localStorage.removeItem("gdpr_nordic_is_demo");
  } catch {
    /* */
  }
}

export function isDemoSession(): boolean {
  try {
    return localStorage.getItem("gdpr_nordic_is_demo") === "1";
  } catch {
    return false;
  }
}

export type BillingPlan = {
  id: string;
  name: string;
  label: string;
  amount: string;
  currency: string;
  intervalLabel: string;
  oneTime?: boolean;
  maxMembers?: number;
};

export async function listBillingPlans() {
  return req<{ data: BillingPlan[]; meta: { configured?: boolean } }>(`/v1/billing/plans`);
}

export async function startBillingCheckout(planId: string, email?: string) {
  return req<{
    data: {
      token: string;
      approveUrl: string;
      plan: { id: string; name: string; amount: string; currency: string };
    };
  }>(`/v1/billing/checkout`, {
    method: "POST",
    body: JSON.stringify({
      planId,
      email,
      successUrl: "https://gdpr-nordic.org/checkout-success.html",
      cancelUrl: "https://gdpr-nordic.org/checkout-cancel.html",
    }),
  });
}

export async function cancelBillingSubscription(profileId: string) {
  return req<{ data: { profileId: string; status: string } }>(
    `/v1/billing/cancel-subscription`,
    {
      method: "POST",
      body: JSON.stringify({ profileId }),
    }
  );
}

// ── Personer på kontot (Familj) ──────────────────────────────────────────
//
// Familj-sidan lade tidigare bara till medlemmar i lokalt React-state.
// Gränsen (Familj = 4) kontrolleras på servern, inte här.

export type Subject = {
  id: string;
  fullName: string;
  email?: string | null;
  city?: string | null;
  createdAt?: number;
};

export async function listSubjects() {
  return req<{ data: Subject[]; meta: { used: number; max: number; plan: string | null } }>(
    `/v1/subjects`,
    { headers: authHeaders() },
  );
}

export async function createSubject(body: {
  fullName: string;
  email?: string;
  city?: string;
}) {
  return req<{ data: Subject }>(`/v1/subjects`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
}

export async function deleteSubject(id: string) {
  return req<{ data: { deleted: boolean } }>(`/v1/subjects/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
}

// ── Bevakning ────────────────────────────────────────────────────────────

export type Preferences = {
  monitorFreq: "weekly" | "daily";
  notifyEmail: boolean;
  notifyPush: boolean;
  locale: string;
};

export async function getPreferences() {
  return req<{ data: Preferences }>(`/v1/me/preferences`, { headers: authHeaders() });
}

export async function savePreferences(patch: Partial<Preferences>) {
  return req<{ data: Preferences }>(`/v1/me/preferences`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(patch),
  });
}

export type MonitorRun = {
  id: string;
  subjectId: string;
  startedAt: number;
  finishedAt: number | null;
  sources: number;
  alerts: number;
  status: string;
};

export async function listMonitorRuns() {
  return req<{ data: MonitorRun[]; meta: { monitored: boolean; freq: string } }>(
    `/v1/monitor/runs`,
    { headers: authHeaders() },
  );
}
